import {
  Account,
  AccountAddress,
  CommittedTransactionResponse,
  InputGenerateTransactionPayloadData,
} from "@aptos-labs/ts-sdk";

import { BaseSDK, Options, SendTxOpts } from "./base";
import { DecibelConfig } from "./constants";
import {
  buildClaimUnlockPayload,
  BuildClaimUnlockPayloadArgs,
  buildOpenTrialPayload,
  BuildOpenTrialPayloadArgs,
  buildSettleTrialPayload,
  BuildSettleTrialPayloadArgs,
} from "./funded-first-trade/payloads";
import {
  ORDER_EVENT_TYPES,
  OrderEvent,
  PlaceOrderResult,
  PlaceSpotOrderResult,
  SpotOrderPendingCbsEvent,
  TwapEvent,
} from "./order-event.types";
import { OrderStatusClient } from "./order-status";
import {
  ActivateVaultArgs,
  CreateVaultArgs,
  DepositToVaultArgs,
  WithdrawFromVaultArgs,
} from "./read";
import { RenameSubaccountArgs, RenameSubaccountSchema } from "./subaccount-types";
import { failedDecryptionFlags, getTransactionSubmissionFailure } from "./submission-error";
import {
  addressesEqual,
  getMarketAddr,
  getPrimarySubaccountAddr,
  getSpotMarketAddr,
  postRequest,
} from "./utils";

export const TimeInForce = {
  GoodTillCanceled: 0,
  PostOnly: 1,
  ImmediateOrCancel: 2,
} as const;
export type TimeInForce = (typeof TimeInForce)[keyof typeof TimeInForce];

interface Cache {
  usdcDecimals?: number;
}

type WithSignerAddress<T> = T & {
  signerAddress: AccountAddress;
};

/** Spot markets are addressed by name (derived) or by object address directly. */
export type SpotMarketRef = { marketName: string } | { marketAddr: string };

/** Per-call submission concerns shared by every subaccount-scoped write method. */
export interface WriteSubmissionOpts extends SendTxOpts {
  /** Subaccount to act under. Defaults to the signer's primary subaccount. */
  subaccountAddr?: string;
  /**
   * Submit this transaction mempool-private (encrypted). Overrides the
   * constructor-level `defaultEncrypted` setting for this call. Silently
   * falls back to unencrypted when the network doesn't support it.
   */
  encrypted?: boolean;
}

/**
 * Rounds price to the nearest tick size multiple
 * @param price The price to round
 * @param tickSize The market's tick size
 * @returns Price rounded to nearest tick size multiple
 */
function roundToTickSize(price: number, tickSize: number): number {
  if (price === 0 || tickSize === 0) return 0;
  return Math.round(price / tickSize) * tickSize;
}

/**
 * Round a price to the tick in the SIDE-SAFE direction for a limit/IOC
 * bound: buys round down (never pay above the caller's cap), sells round up
 * (never accept below the caller's floor). Nearest-tick rounding would let a
 * non-aligned cap cross the user's configured limit/slippage.
 *
 * The epsilon absorbs IEEE-754 division noise so an already-aligned price
 * stays put instead of dropping a whole tick. It is RELATIVE to the tick
 * count, because float error is: a fixed absolute epsilon is simultaneously
 * too wide at ordinary magnitudes (rounding a buy up past a price genuinely
 * below the tick) and too narrow at large ones — a BTC-scale spot price
 * ($100k at 6 quote decimals, 100-unit tick) is 1e9 ticks, where drift
 * already exceeds 1e-9.
 *
 * {@link DecibelWriteDex.placeSpotOrder} applies this internally when given a
 * `tickSize`, and it is idempotent, so pre-rounding does not change the
 * submitted value.
 */
export function roundToTickSizeForSide(price: number, tickSize: number, isBuy: boolean): number {
  if (price === 0 || tickSize === 0) return 0;
  const ticks = price / tickSize;
  const epsilon = Math.abs(ticks) * Number.EPSILON * 8;
  const rounded = isBuy ? Math.floor(ticks + epsilon) : Math.ceil(ticks - epsilon);
  return rounded * tickSize;
}

/**
 * Converts a basis point (bps) to chain units
 * @param bps The basis point value (e.g. 10 for 0.1%)
 * @returns The basis point value in chain units
 */
function bpsToChainUnits(bps: number): number {
  // From contract:
  // const FEE_PRECISION: u64 = 10000; // 10000 => 1%
  // Since a basis point is 1/100th of a percent, we multiply by 100 to get the chain units.
  return Math.round(bps * 100);
}

export class DecibelWriteDex extends BaseSDK {
  readonly cache: Cache;
  readonly orderStatusClient: OrderStatusClient;
  /**
   * Whether front-run-sensitive writes from this instance are submitted
   * encrypted by default. Public so a host app can tell the user what the next
   * write will actually do instead of maintaining its own copy of the decision.
   * Still subject to the network-capability fallback in `sendEncryptedTx`.
   */
  readonly defaultEncrypted: boolean;

  constructor(config: DecibelConfig, account: Account, opts?: Options) {
    super(config, account, opts);
    this.cache = {};
    this.orderStatusClient = new OrderStatusClient(config);
    this.defaultEncrypted = opts?.defaultEncrypted ?? false;
  }

  /**
   * Extract order_id from OrderEvent in transaction response
   */
  extractOrderIdFromTransaction(
    txResponse: CommittedTransactionResponse,
    subaccountAddr?: string,
  ): string | null {
    const orderEvents = Object.values(ORDER_EVENT_TYPES);
    // With no subaccount the order goes to the primary one, not the owner.
    const placedBy =
      subaccountAddr || this.getPrimarySubaccountAddress(this.account.accountAddress);
    try {
      // Check if the response is a UserTransactionResponse with events
      if ("events" in txResponse && Array.isArray(txResponse.events)) {
        for (const event of txResponse.events) {
          // Check if this is an OrderEvent from the market module
          for (const orderEvent of orderEvents) {
            if (event.type.includes(orderEvent)) {
              const orderEvent = event.data as OrderEvent | TwapEvent;
              // Only one of these exists per event type.
              const { user } = orderEvent as OrderEvent;
              const { account } = orderEvent as TwapEvent;
              if (
                (user && addressesEqual(user, placedBy)) ||
                (account && addressesEqual(account, placedBy))
              ) {
                return typeof orderEvent.order_id === "string"
                  ? orderEvent.order_id
                  : orderEvent.order_id.order_id;
              }
            }
          }
        }
      }
      return null;
    } catch (error) {
      console.error("Error extracting order_id from transaction:", error);
      return null;
    }
  }

  async renameSubaccount({ subaccountAddress, newName }: RenameSubaccountArgs) {
    return await postRequest({
      schema: RenameSubaccountSchema,
      url: `${this.config.tradingHttpUrl}/api/v1/subaccounts/${subaccountAddress}`,
      body: { name: newName },
    });
  }

  async createSubaccount() {
    return await this.sendTx({
      function: `${this.config.deployment.package}::dex_accounts_entry::create_new_subaccount`,
      typeArguments: [],
      functionArguments: [],
    });
  }

  /**
   * Admin: create a new subaccount for the given owner address.
   * The signer (this.account) must be an admin.
   * @param ownerAddress The address of the owner to create a subaccount for
   */
  async adminCreateSubaccount(ownerAddress: string) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::dex_accounts_entry::admin_create_new_subaccount`,
      typeArguments: [],
      functionArguments: [ownerAddress],
    });
  }

  async sendSubaccountTx(
    sendTx: (subaccountAddr: string) => Promise<CommittedTransactionResponse>,
    subaccountAddr?: string,
  ) {
    if (!subaccountAddr) {
      subaccountAddr = getPrimarySubaccountAddr(
        this.account.accountAddress,
        this.config.compatVersion,
        this.config.deployment.package,
      );
    }
    return await sendTx(subaccountAddr);
  }

  private async submitSubaccountTx(
    payloadFn: (subaccountAddr: string) => InputGenerateTransactionPayloadData,
    { subaccountAddr, encrypted, ...sendOpts }: WriteSubmissionOpts = {},
  ) {
    const shouldEncrypt = encrypted ?? this.defaultEncrypted;
    return await this.sendSubaccountTx(
      (addr) =>
        shouldEncrypt
          ? this.sendEncryptedTx(payloadFn(addr), sendOpts)
          : this.sendTx(payloadFn(addr), sendOpts),
      subaccountAddr,
    );
  }

  async withSubaccount<T>(fn: (subaccountAddr: string) => Promise<T>, subaccountAddr?: string) {
    if (!subaccountAddr) {
      subaccountAddr = getPrimarySubaccountAddr(
        this.account.accountAddress,
        this.config.compatVersion,
        this.config.deployment.package,
      );
    }
    return await fn(subaccountAddr);
  }
  /**
   * @param amount u64 amount of collateral to deposit
   */
  async deposit(amount: number, subaccountAddr?: string) {
    return await this.depositCollateral(this.config.deployment.usdc, amount, subaccountAddr);
  }

  /**
   * @param amount u64 amount of collateral to withdraw
   */
  async withdraw(amount: number, subaccountAddr?: string) {
    return await this.withdrawCollateral(this.config.deployment.usdc, amount, subaccountAddr);
  }

  async withdrawNonCollateral(assetAddr: string, amount: number, subaccountAddr?: string) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::withdraw_from_non_collateral`,
          typeArguments: [],
          functionArguments: [subaccountAddr, assetAddr, amount],
        }),
      subaccountAddr,
    );
  }

  /** Mints non-transferable dLEND shares from wallet USDC. */
  async supplyToLenderPool(amount: number) {
    return await this.sendTx({
      function: `${this.config.deployment.package}::account_management_apis::supply_to_lender_pool`,
      typeArguments: [],
      functionArguments: [amount],
    });
  }

  /** Credits USDC to a perp-initialized subaccount. */
  async redeemFromLenderPool(shareAmount: number, subaccountAddr?: string) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::account_management_apis::redeem_from_lender_pool_entry`,
          typeArguments: [],
          functionArguments: [subaccountAddr, shareAmount],
        }),
      subaccountAddr,
    );
  }

  /** Registered borrow-lend assets are pledged as collateral. */
  async depositCollateral(assetAddr: string, amount: number, subaccountAddr?: string) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::deposit_to_subaccount_at`,
          typeArguments: [],
          functionArguments: [subaccountAddr, assetAddr, amount],
        }),
      subaccountAddr,
    );
  }

  async withdrawCollateral(assetAddr: string, amount: number, subaccountAddr?: string) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::withdraw_from_cross_collateral`,
          typeArguments: [],
          functionArguments: [subaccountAddr, assetAddr, amount],
        }),
      subaccountAddr,
    );
  }

  /** Uses free cross margin; the amount is clamped to outstanding debt. */
  async repayUsdc(amount: number, subaccountAddr?: string) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::repay_usdc_from_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddr, amount],
        }),
      subaccountAddr,
    );
  }

  async configureUserSettingsForMarket({
    marketAddr,
    subaccountAddr,
    isCross,
    userLeverage,
  }: {
    marketAddr: string;
    subaccountAddr: string;
    isCross: boolean;
    userLeverage: number;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::configure_user_settings_for_market`,
          typeArguments: [],
          functionArguments: [subaccountAddr, marketAddr, isCross, userLeverage],
        }),
      subaccountAddr,
    );
  }

  async placeOrder({
    marketName,
    price,
    size,
    isBuy,
    timeInForce,
    isReduceOnly,
    clientOrderId,
    stopPrice,
    tpTriggerPrice,
    tpLimitPrice,
    slTriggerPrice,
    slLimitPrice,
    builderAddr,
    builderFee,
    tickSize,
    ...submitOpts
  }: {
    marketName: string;
    price: number;
    size: number;
    isBuy: boolean;
    timeInForce: TimeInForce;
    isReduceOnly: boolean;
    clientOrderId?: string;
    stopPrice?: number;
    tpTriggerPrice?: number;
    tpLimitPrice?: number;
    slTriggerPrice?: number;
    slLimitPrice?: number;
    builderAddr?: string;
    builderFee?: number;
    /** Market tick size for price rounding. If not provided, no rounding is applied. */
    tickSize?: number;
  } & WriteSubmissionOpts): Promise<PlaceOrderResult> {
    try {
      const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);

      // Apply tick size rounding if tickSize is provided
      const roundedPrice = tickSize ? roundToTickSize(price, tickSize) : price;
      const roundedStopPrice =
        stopPrice !== undefined && tickSize ? roundToTickSize(stopPrice, tickSize) : stopPrice;
      const roundedTpTriggerPrice =
        tpTriggerPrice !== undefined && tickSize
          ? roundToTickSize(tpTriggerPrice, tickSize)
          : tpTriggerPrice;
      const roundedTpLimitPrice =
        tpLimitPrice !== undefined && tickSize
          ? roundToTickSize(tpLimitPrice, tickSize)
          : tpLimitPrice;
      const roundedSlTriggerPrice =
        slTriggerPrice !== undefined && tickSize
          ? roundToTickSize(slTriggerPrice, tickSize)
          : slTriggerPrice;
      const roundedSlLimitPrice =
        slLimitPrice !== undefined && tickSize
          ? roundToTickSize(slLimitPrice, tickSize)
          : slLimitPrice;

      const txResponse = await this.submitSubaccountTx(
        (subaccountAddr) => ({
          function: `${this.config.deployment.package}::dex_accounts_entry::place_order_to_subaccount`,
          typeArguments: [],
          functionArguments: [
            subaccountAddr,
            marketAddr.toString(),
            roundedPrice,
            size,
            isBuy,
            timeInForce,
            isReduceOnly,
            clientOrderId,
            roundedStopPrice,
            roundedTpTriggerPrice,
            roundedTpLimitPrice,
            roundedSlTriggerPrice,
            roundedSlLimitPrice,
            builderAddr,
            builderFee ? bpsToChainUnits(builderFee) : undefined,
          ],
        }),
        submitOpts,
      );

      // Extract order_id from the transaction events
      const orderId = this.extractOrderIdFromTransaction(txResponse, submitOpts.subaccountAddr);

      return {
        success: true,
        orderId: orderId || undefined,
        transactionHash: txResponse.hash,
      };
    } catch (error) {
      console.error("Error placing order:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
        submission: getTransactionSubmissionFailure(error),
        ...failedDecryptionFlags(error),
      };
    }
  }

  async triggerMatching({ marketAddr, maxWorkUnit }: { marketAddr: string; maxWorkUnit: number }) {
    const txResponse = await this.sendTx({
      function: `${this.config.deployment.package}::public_apis::process_perp_market_pending_requests`,
      typeArguments: [],
      functionArguments: [marketAddr, maxWorkUnit],
    });
    return {
      success: true,
      transactionHash: txResponse.hash,
    };
  }

  async placeTwapOrder({
    marketName,
    size,
    isBuy,
    isReduceOnly,
    clientOrderId,
    twapFrequencySeconds,
    twapDurationSeconds,
    builderAddress,
    builderFees,
    ...submitOpts
  }: {
    marketName: string;
    size: number;
    isBuy: boolean;
    isReduceOnly: boolean;
    clientOrderId?: string;
    twapFrequencySeconds: number;
    twapDurationSeconds: number;
    builderAddress?: string;
    builderFees?: number;
  } & WriteSubmissionOpts) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    const txResponse = await this.submitSubaccountTx(
      (subaccountAddr) => ({
        // TODO: update to place_twap_order_to_subaccount_v2 once available
        function: `${this.config.deployment.package}::dex_accounts_entry::place_twap_order_to_subaccount_v2`,
        typeArguments: [],
        functionArguments: [
          subaccountAddr,
          marketAddr.toString(),
          size,
          isBuy,
          isReduceOnly,
          clientOrderId, // TODO: include once v2 is available
          twapFrequencySeconds,
          twapDurationSeconds,
          builderAddress,
          builderFees ? bpsToChainUnits(builderFees) : undefined,
        ],
      }),
      submitOpts,
    );

    const orderId = this.extractOrderIdFromTransaction(txResponse, submitOpts.subaccountAddr);

    return {
      success: true,
      orderId: orderId || undefined,
      transactionHash: txResponse.hash,
    };
  }

  /**
   * Cancel an order on the exchange
   * @param orderId The id of the order to cancel
   * @param marketId The id of the market the order is in
   * @param subaccountAddr Optional subaccount address, will use primary if not provided
   * @returns Transaction response
   */
  async cancelOrder(
    args: { orderId: number | string } & WriteSubmissionOpts &
      ({ marketName: string } | { marketAddr: string }),
  ) {
    const marketAddr =
      "marketName" in args
        ? getMarketAddr(args.marketName, this.config.deployment.perpEngineGlobal)
        : args.marketAddr;

    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::cancel_order_to_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, BigInt(args.orderId.toString()), marketAddr.toString()],
      }),
      {
        subaccountAddr: args.subaccountAddr,
        accountOverride: args.accountOverride,
        encrypted: args.encrypted,
      },
    );
  }

  async cancelClientOrder({
    clientOrderId,
    marketName,
    ...submitOpts
  }: {
    clientOrderId: string;
    marketName: string;
  } & WriteSubmissionOpts) {
    const marketAddr = getMarketAddr(marketName, this.config.deployment.perpEngineGlobal);
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::cancel_client_order_to_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, clientOrderId, marketAddr.toString()],
      }),
      submitOpts,
    );
  }

  async cancelBulkOrder(
    args: WriteSubmissionOpts & ({ marketName: string } | { marketAddr: string }),
  ) {
    const marketAddr =
      "marketName" in args
        ? getMarketAddr(args.marketName, this.config.deployment.perpEngineGlobal)
        : args.marketAddr;

    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::cancel_bulk_order_to_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, marketAddr.toString()],
      }),
      {
        subaccountAddr: args.subaccountAddr,
        accountOverride: args.accountOverride,
        encrypted: args.encrypted,
      },
    );
  }

  // ======= SPOT TRADING =======
  //
  // All spot methods are subaccount-scoped (defaulting to the signer's primary
  // subaccount), mirroring the perp write surface. The wallet-direct entry
  // functions (`place_spot_order`, `place_spot_bulk_order`, ...) are not
  // exposed here.

  private resolveSpotMarketAddr(ref: SpotMarketRef): string {
    return "marketName" in ref
      ? getSpotMarketAddr(ref.marketName, this.config.deployment.package).toString()
      : ref.marketAddr;
  }

  /**
   * Spot placement is CBS-backed and async: when funding needs a rate-limited
   * CBS withdrawal the transaction succeeds but the order is only queued
   * (SpotOrderPendingCbsEvent) rather than resting (OrderEvent).
   */
  private extractSpotOrderPlacement(
    txResponse: CommittedTransactionResponse,
    subaccountAddr?: string,
  ): { orderId: string | undefined; pendingCbs: boolean } {
    const expectedSubaccount =
      subaccountAddr ?? this.getPrimarySubaccountAddress(this.account.accountAddress);
    if ("events" in txResponse && Array.isArray(txResponse.events)) {
      for (const event of txResponse.events) {
        if (event.type.includes("market_types::OrderEvent")) {
          const data = event.data as OrderEvent;
          if (addressesEqual(data.user, expectedSubaccount)) {
            return { orderId: data.order_id, pendingCbs: false };
          }
        }
        if (event.type.includes("spot_pending_cbs_queue::SpotOrderPendingCbsEvent")) {
          const data = event.data as SpotOrderPendingCbsEvent;
          if (addressesEqual(data.subaccount_addr, expectedSubaccount)) {
            return { orderId: data.order_id, pendingCbs: true };
          }
        }
      }
    }
    return { orderId: undefined, pendingCbs: false };
  }

  async placeSpotOrder(
    args: {
      price: number;
      size: number;
      isBuy: boolean;
      timeInForce: TimeInForce;
      builderAddr?: string;
      builderFee?: number;
      /** Market tick size for price rounding. If not provided, no rounding is applied. */
      tickSize?: number;
    } & SpotMarketRef &
      WriteSubmissionOpts,
  ): Promise<PlaceSpotOrderResult> {
    try {
      const marketAddr = this.resolveSpotMarketAddr(args);
      // Side-safe, not nearest: spot prices are limit/IOC bounds, so rounding
      // must never cross the caller's cap (buy) or floor (sell).
      const price = args.tickSize
        ? roundToTickSizeForSide(args.price, args.tickSize, args.isBuy)
        : args.price;

      const txResponse = await this.submitSubaccountTx(
        (subaccountAddr) => ({
          function: `${this.config.deployment.package}::dex_accounts_spot_entry::place_spot_order_to_subaccount`,
          typeArguments: [],
          functionArguments: [
            subaccountAddr,
            marketAddr,
            price,
            args.size,
            args.isBuy,
            args.timeInForce,
            args.builderAddr,
            args.builderFee !== undefined ? bpsToChainUnits(args.builderFee) : undefined,
          ],
        }),
        {
          subaccountAddr: args.subaccountAddr,
          accountOverride: args.accountOverride,
          encrypted: args.encrypted,
        },
      );

      const { orderId, pendingCbs } = this.extractSpotOrderPlacement(
        txResponse,
        args.subaccountAddr,
      );
      return { success: true, orderId, pendingCbs, transactionHash: txResponse.hash };
    } catch (error) {
      console.error("Error placing spot order:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
        ...failedDecryptionFlags(error),
      };
    }
  }

  async cancelSpotOrder(args: { orderId: number | string } & SpotMarketRef & WriteSubmissionOpts) {
    const marketAddr = this.resolveSpotMarketAddr(args);
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_spot_entry::cancel_spot_order_to_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, marketAddr, BigInt(args.orderId.toString())],
      }),
      {
        subaccountAddr: args.subaccountAddr,
        accountOverride: args.accountOverride,
        encrypted: args.encrypted,
      },
    );
  }

  /**
   * Place (or replace) a spot bulk order. Funds are sourced from PFS
   * first, then CBS (sync only — a rate-limited CBS withdrawal aborts).
   * `sequenceNumber` must be strictly increasing per market.
   */
  async placeSpotBulkOrder(
    args: {
      sequenceNumber: number;
      bidPrices: number[];
      bidSizes: number[];
      askPrices: number[];
      askSizes: number[];
      builderAddr?: string;
      builderFee?: number;
    } & SpotMarketRef &
      WriteSubmissionOpts,
  ) {
    const marketAddr = this.resolveSpotMarketAddr(args);
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_spot_entry::place_spot_bulk_order_to_subaccount`,
        typeArguments: [],
        functionArguments: [
          subaccountAddr,
          marketAddr,
          args.sequenceNumber,
          args.bidPrices,
          args.bidSizes,
          args.askPrices,
          args.askSizes,
          args.builderAddr,
          args.builderFee !== undefined ? bpsToChainUnits(args.builderFee) : undefined,
        ],
      }),
      {
        subaccountAddr: args.subaccountAddr,
        accountOverride: args.accountOverride,
        encrypted: args.encrypted,
      },
    );
  }

  async cancelSpotBulkOrder(args: SpotMarketRef & WriteSubmissionOpts) {
    const marketAddr = this.resolveSpotMarketAddr(args);
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_spot_entry::cancel_spot_bulk_order_to_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, marketAddr],
      }),
      {
        subaccountAddr: args.subaccountAddr,
        accountOverride: args.accountOverride,
        encrypted: args.encrypted,
      },
    );
  }

  async cancelSpotBulkOrderAtPriceLevel(
    args: { price: number; isBuy: boolean } & SpotMarketRef & WriteSubmissionOpts,
  ) {
    const marketAddr = this.resolveSpotMarketAddr(args);
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_spot_entry::cancel_spot_bulk_order_at_price_level_to_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, marketAddr, args.price, args.isBuy],
      }),
      {
        subaccountAddr: args.subaccountAddr,
        accountOverride: args.accountOverride,
        encrypted: args.encrypted,
      },
    );
  }

  /**
   * Approve a per-builder max fee for spot orders from this subaccount.
   * Unlike perp, the builder address may be a subaccount or a primary wallet.
   * @param maxFee The maximum fee in basis points (e.g. 10 = 0.1%)
   */
  async approveMaxSpotBuilderFee({
    builderAddr,
    maxFee,
    subaccountAddr,
  }: {
    builderAddr: string;
    maxFee: number;
    subaccountAddr?: string;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_spot_entry::approve_max_spot_builder_fee_for_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddr, builderAddr, bpsToChainUnits(maxFee)],
        }),
      subaccountAddr,
    );
  }

  /** Revoke a prior spot builder-fee approval made by the subaccount. */
  async revokeMaxSpotBuilderFee({
    builderAddr,
    subaccountAddr,
  }: {
    builderAddr: string;
    subaccountAddr?: string;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_spot_entry::revoke_max_spot_builder_fee_for_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddr, builderAddr],
        }),
      subaccountAddr,
    );
  }

  /**
   * Set HOLD_AS_NON_COLLATERAL for an asset on the subaccount. When enabled,
   * future deposits of the asset stay in the PFS (non-collateral) instead of
   * routing into CBS collateral. Flag-only: existing balances are not moved.
   */
  async setHoldAsNonCollateral({
    assetAddr,
    hold,
    subaccountAddr,
  }: {
    assetAddr: string;
    hold: boolean;
    subaccountAddr?: string;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_spot_entry::set_hold_as_non_collateral_for_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddr, assetAddr, hold],
        }),
      subaccountAddr,
    );
  }

  /** Crank pending async spot matching requests — permissionless, no subaccount. */
  async processSpotPendingRequests(args: { maxFills: number } & SpotMarketRef) {
    const marketAddr = this.resolveSpotMarketAddr(args);
    const txResponse = await this.sendTx({
      function: `${this.config.deployment.package}::dex_accounts_spot_entry::process_spot_pending_requests`,
      typeArguments: [],
      functionArguments: [marketAddr, args.maxFills],
    });
    return {
      success: true,
      transactionHash: txResponse.hash,
    };
  }

  async delegateTradingToForSubaccount({
    subaccountAddr,
    accountToDelegateTo,
    expirationTimestampSecs,
  }: {
    subaccountAddr: string;
    accountToDelegateTo: string;
    expirationTimestampSecs?: number;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::delegate_trading_to_for_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddr, accountToDelegateTo, expirationTimestampSecs ?? null],
        }),
      subaccountAddr,
    );
  }

  async revokeDelegation({
    subaccountAddr,
    accountToRevoke,
  }: {
    subaccountAddr?: string;
    accountToRevoke: string;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::revoke_delegation`,
          typeArguments: [],
          functionArguments: [subaccountAddr, accountToRevoke],
        }),
      subaccountAddr,
    );
  }

  /**
   * Place a TP/SL order for a position
   */
  async placeTpSlOrderForPosition({
    marketAddr,
    tpTriggerPrice,
    tpLimitPrice,
    tpSize,
    slTriggerPrice,
    slLimitPrice,
    slSize,
    tickSize,
    ...submitOpts
  }: {
    marketAddr: string;
    tpTriggerPrice?: number;
    tpLimitPrice?: number;
    tpSize?: number;
    slTriggerPrice?: number;
    slLimitPrice?: number;
    slSize?: number;
    tickSize?: number;
  } & WriteSubmissionOpts) {
    const roundedTpTriggerPrice =
      tpTriggerPrice !== undefined && tickSize
        ? roundToTickSize(tpTriggerPrice, tickSize)
        : tpTriggerPrice;
    const roundedTpLimitPrice =
      tpLimitPrice !== undefined && tickSize
        ? roundToTickSize(tpLimitPrice, tickSize)
        : tpLimitPrice;
    const roundedSlTriggerPrice =
      slTriggerPrice !== undefined && tickSize
        ? roundToTickSize(slTriggerPrice, tickSize)
        : slTriggerPrice;
    const roundedSlLimitPrice =
      slLimitPrice !== undefined && tickSize
        ? roundToTickSize(slLimitPrice, tickSize)
        : slLimitPrice;

    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::place_tp_sl_order_for_position`,
        typeArguments: [],
        functionArguments: [
          subaccountAddr,
          marketAddr,
          roundedTpTriggerPrice,
          roundedTpLimitPrice,
          tpSize,
          roundedSlTriggerPrice,
          roundedSlLimitPrice,
          slSize,
          undefined, // builderAddr
          undefined, // builderFees
        ],
      }),
      submitOpts,
    );
  }

  /**
   * Update TP for a position
   */
  async updateTpOrderForPosition({
    marketAddr,
    prevOrderId,
    tpTriggerPrice,
    tpLimitPrice,
    tpSize,
    tickSize,
    ...submitOpts
  }: {
    marketAddr: string;
    prevOrderId: number | string;
    tpTriggerPrice?: number;
    tpLimitPrice?: number;
    tpSize?: number;
    tickSize?: number;
  } & WriteSubmissionOpts) {
    const roundedTpTriggerPrice =
      tpTriggerPrice !== undefined && tickSize
        ? roundToTickSize(tpTriggerPrice, tickSize)
        : tpTriggerPrice;
    const roundedTpLimitPrice =
      tpLimitPrice !== undefined && tickSize
        ? roundToTickSize(tpLimitPrice, tickSize)
        : tpLimitPrice;

    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::update_tp_order_for_position`,
        typeArguments: [],
        functionArguments: [
          subaccountAddr,
          BigInt(prevOrderId.toString()),
          marketAddr,
          roundedTpTriggerPrice,
          roundedTpLimitPrice,
          tpSize,
        ],
      }),
      submitOpts,
    );
  }

  /**
   * Update SL for a position
   */
  async updateSlOrderForPosition({
    marketAddr,
    prevOrderId,
    slTriggerPrice,
    slLimitPrice,
    slSize,
    tickSize,
    ...submitOpts
  }: {
    marketAddr: string;
    prevOrderId: number | string;
    slTriggerPrice?: number;
    slLimitPrice?: number;
    slSize?: number;
    tickSize?: number;
  } & WriteSubmissionOpts) {
    const roundedSlTriggerPrice =
      slTriggerPrice !== undefined && tickSize
        ? roundToTickSize(slTriggerPrice, tickSize)
        : slTriggerPrice;
    const roundedSlLimitPrice =
      slLimitPrice !== undefined && tickSize
        ? roundToTickSize(slLimitPrice, tickSize)
        : slLimitPrice;

    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::update_sl_order_for_position`,
        typeArguments: [],
        functionArguments: [
          subaccountAddr,
          BigInt(prevOrderId.toString()),
          marketAddr,
          roundedSlTriggerPrice,
          roundedSlLimitPrice,
          slSize,
        ],
      }),
      submitOpts,
    );
  }

  /**
   * Cancel a TP/SL order for a position
   */
  async cancelTpSlOrderForPosition({
    marketAddr,
    orderId,
    ...submitOpts
  }: {
    marketAddr: string;
    orderId: number | string;
  } & WriteSubmissionOpts) {
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::cancel_tp_sl_order_for_position`,
        typeArguments: [],
        functionArguments: [subaccountAddr, marketAddr, BigInt(orderId.toString())],
      }),
      submitOpts,
    );
  }

  /**
   * Update an existing order's parameters including TP/SL.
   * Pass undefined for tp/sl fields to remove them from the order.
   * @aptos-labs/ts-sdk treats null/undefined as Move Option::None.
   */
  async updateOrder({
    orderId,
    marketAddr,
    price,
    size,
    isBuy,
    timeInForce,
    isReduceOnly,
    tpTriggerPrice,
    tpLimitPrice,
    slTriggerPrice,
    slLimitPrice,
    ...submitOpts
  }: {
    orderId: number | string;
    marketAddr: string;
    price: number;
    size: number;
    isBuy: boolean;
    timeInForce: TimeInForce;
    isReduceOnly: boolean;
    tpTriggerPrice?: number;
    tpLimitPrice?: number;
    slTriggerPrice?: number;
    slLimitPrice?: number;
  } & WriteSubmissionOpts) {
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::update_order_to_subaccount`,
        typeArguments: [],
        functionArguments: [
          subaccountAddr,
          BigInt(orderId.toString()),
          marketAddr,
          price,
          size,
          isBuy,
          timeInForce,
          isReduceOnly,
          tpTriggerPrice,
          tpLimitPrice,
          slTriggerPrice,
          slLimitPrice,
          undefined, // builder_address
          undefined, // builder_fees
        ],
      }),
      submitOpts,
    );
  }

  async cancelTwapOrder({
    orderId,
    marketAddr,
    ...submitOpts
  }: {
    orderId: string;
    marketAddr: string;
  } & WriteSubmissionOpts) {
    return await this.submitSubaccountTx(
      (subaccountAddr) => ({
        function: `${this.config.deployment.package}::dex_accounts_entry::cancel_twap_orders_to_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, marketAddr, orderId],
      }),
      submitOpts,
    );
  }

  async buildDeactiveSubaccountTx({
    subaccountAddr,
    revokeAllDelegations = true,
    signerAddress,
  }: WithSignerAddress<{
    subaccountAddr: string;
    /** @default true */
    revokeAllDelegations?: boolean;
  }>) {
    const transaction = await this.buildTx(
      {
        function: `${this.config.deployment.package}::dex_accounts_entry::deactivate_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddr, revokeAllDelegations],
      },
      signerAddress,
    );
    return transaction;
  }

  // ======= VAULT FUNCTIONS =======

  // @Todo: We can move this to another Class since it does not require a subaccount
  /**
   * Create a new vault with optional initial funding
   */
  async buildCreateVaultTx({
    contributionAssetType,
    vaultName,
    vaultShareSymbol,
    vaultShareIconUri = "",
    vaultShareProjectUri = "",
    feeBps,
    feeIntervalS,
    contributionLockupDurationS,
    initialFunding = 0,
    acceptsContributions = false,
    delegateToCreator = false,
    signerAddress,
    vaultDescription,
    vaultSocialLinks,
  }: WithSignerAddress<CreateVaultArgs>) {
    const signerPrimarySubaccount = this.getPrimarySubaccountAddress(signerAddress);

    const transaction = await this.buildTx(
      {
        function: `${this.config.deployment.package}::vault_api::create_and_fund_vault`,
        typeArguments: [],
        functionArguments: [
          signerPrimarySubaccount,
          contributionAssetType,
          vaultName,
          vaultDescription,
          vaultSocialLinks,
          vaultShareSymbol,
          vaultShareIconUri,
          vaultShareProjectUri,
          feeBps,
          feeIntervalS,
          contributionLockupDurationS,
          initialFunding,
          acceptsContributions,
          delegateToCreator,
        ],
      },
      signerAddress,
    );

    return transaction;
  }

  async createVault(
    args: CreateVaultArgs & {
      /**
       * Optional account to use for the transaction. Primarily set as the session
       * account.  If not provided, the default constructor account will be used
       */
      accountOverride?: Account;
      subaccountAddr?: string;
    },
  ) {
    const txResponse = await this.sendSubaccountTx(
      () =>
        this.sendTx(
          {
            function: `${this.config.deployment.package}::vault_api::create_and_fund_vault`,
            typeArguments: [],
            functionArguments: [
              args.subaccountAddr ??
                this.getPrimarySubaccountAddress(
                  (args.accountOverride ?? this.account).accountAddress,
                ),
              args.contributionAssetType,
              args.vaultName,
              args.vaultDescription,
              args.vaultSocialLinks,
              args.vaultShareSymbol,
              args.vaultShareIconUri,
              args.vaultShareProjectUri,
              args.feeBps,
              args.feeIntervalS,
              args.contributionLockupDurationS,
              args.initialFunding ?? 0,
              args.acceptsContributions ?? false,
              args.delegateToCreator ?? false,
            ],
          },
          { accountOverride: args.accountOverride },
        ),
      args.subaccountAddr,
    );

    return txResponse;
  }

  /**
   * Activate a vault to accept contributions
   */
  async buildActivateVaultTx({
    vaultAddress,
    signerAddress,
  }: WithSignerAddress<ActivateVaultArgs>) {
    return await this.buildTx(
      {
        function: `${this.config.deployment.package}::vault_api::activate_vault`,
        typeArguments: [],
        functionArguments: [vaultAddress],
      },
      signerAddress,
    );
  }

  /**
   * Contribute funds to a vault in exchange for shares
   */
  async buildDepositToVaultTx({
    vaultAddress,
    amount,
    signerAddress,
  }: WithSignerAddress<DepositToVaultArgs>) {
    return await this.buildTx(
      {
        function: `${this.config.deployment.package}::dex_accounts_entry::contribute_to_vault`,
        typeArguments: [],
        functionArguments: [
          this.getPrimarySubaccountAddress(signerAddress), // todo - select correct subaccount
          vaultAddress,
          this.config.deployment.usdc,
          amount,
        ],
      },
      signerAddress,
    );
  }

  async depositToVault(
    args: DepositToVaultArgs & {
      subaccountAddr: string;
    },
  ) {
    const txResponse = await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::contribute_to_vault`,
          typeArguments: [],
          functionArguments: [
            subaccountAddr,
            args.vaultAddress,
            this.config.deployment.usdc,
            args.amount,
          ],
        }),
      args.subaccountAddr,
    );

    return txResponse;
  }

  async withdrawFromVault(
    args: WithdrawFromVaultArgs & {
      /**
       * Optional account to use for the transaction. Primarily set as the session
       * account.  If not provided, the default constructor account will be used
       */
      accountOverride?: Account;
      subaccountAddr?: string;
    },
  ) {
    const txResponse = await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx(
          {
            function: `${this.config.deployment.package}::dex_accounts_entry::redeem_from_vault`,
            typeArguments: [],
            functionArguments: [subaccountAddr, args.vaultAddress, args.shares],
          },
          { accountOverride: args.accountOverride },
        ),
      args.subaccountAddr,
    );

    return txResponse;
  }
  /**
   * Delegate trading to another account for a vault
   */
  async buildDelegateDexActionsToTx({
    vaultAddress,
    accountToDelegateTo,
    signerAddress,
    expirationTimestampSecs,
  }: WithSignerAddress<{
    vaultAddress: string;
    accountToDelegateTo: string;
    expirationTimestampSecs?: number;
  }>) {
    return await this.buildTx(
      {
        function: `${this.config.deployment.package}::vault_admin_api::delegate_dex_actions_to`,
        typeArguments: [],
        functionArguments: [vaultAddress, accountToDelegateTo, expirationTimestampSecs],
      },
      signerAddress,
    );
  }

  /**
   * Approve max builder fee for a subaccount
   * @param builderAddr The address of the builder
   * @param maxFee The maximum fee in basis points (e.g., 100 = 0.01%)
   * @param subaccountAddr Optional subaccount address, will use primary if not provided
   */
  async approveMaxBuilderFee({
    builderAddr,
    maxFee,
    subaccountAddr,
  }: {
    builderAddr: string;
    maxFee: number;
    subaccountAddr?: string;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::approve_max_builder_fee_for_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddr, builderAddr, bpsToChainUnits(maxFee)],
        }),
      subaccountAddr,
    );
  }

  /**
   * Revoke max builder fee for a subaccount
   * @param builderAddr The address of the builder
   * @param subaccountAddr Optional subaccount address, will use primary if not provided
   */
  async revokeMaxBuilderFee({
    builderAddr,
    subaccountAddr,
  }: {
    builderAddr: string;
    subaccountAddr?: string;
  }) {
    return await this.sendSubaccountTx(
      (subaccountAddr) =>
        this.sendTx({
          function: `${this.config.deployment.package}::dex_accounts_entry::revoke_max_builder_fee_for_subaccount`,
          typeArguments: [],
          functionArguments: [subaccountAddr, builderAddr],
        }),
      subaccountAddr,
    );
  }

  /**
   * Claim reward from a campaign by ID. The signer must have an allocation in the campaign.
   * @param campaignId The numeric ID of the campaign to claim from
   */
  async claimCampaignReward(campaignId: number) {
    return await this.sendTx({
      function: `${this.config.deployment.campaignPackage}::campaign_manager::claim_by_id`,
      typeArguments: [],
      functionArguments: [campaignId],
    });
  }

  /**
   * Open a Funded First Trade trial for `owner`. The signer must be the owner
   * or hold a TradePerpsAllMarkets delegation on the owner's primary
   * subaccount (session key).
   */
  async openFftTrial(args: BuildOpenTrialPayloadArgs) {
    return await this.sendTx(buildOpenTrialPayload(args));
  }

  /** Claim a matured FFT lock for `owner`. Same signer rule as {@link openFftTrial}. */
  async claimFftUnlock(args: BuildClaimUnlockPayloadArgs) {
    return await this.sendTx(buildClaimUnlockPayload(args));
  }

  /** Settle an expired FFT trial — permissionless; any signer works. */
  async settleFftTrial(args: BuildSettleTrialPayloadArgs) {
    return await this.sendTx(buildSettleTrialPayload(args));
  }
}
